'use client';

import AppImage from '@/components/ui/AppImage';
import { ShieldCheck, Sparkles, Globe2, BriefcaseBusiness } from 'lucide-react';

interface ClientLogo {
  id: string;
  name: string;
  logo: string;
  alt: string;
}

interface TrustedBySectionProps {
  title: string;
  subtitle: string;
  clients: ClientLogo[];
}

export default function TrustedBySection({ title, subtitle, clients }: TrustedBySectionProps) {
  return (
    <section className="py-12 md:py-16 lg:py-24 bg-gradient-to-br from-pink-50 via-slate-50 to-blue-50 relative overflow-hidden isolate">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(250,0,172,0.16),transparent_40%),radial-gradient(circle_at_82%_20%,rgba(39,78,255,0.14),transparent_40%),radial-gradient(circle_at_50%_88%,rgba(16,185,129,0.12),transparent_44%)]" />
        <div className="absolute -top-20 -left-24 w-80 h-80 bg-pink-400/20 rounded-full blur-3xl animate-float-soft" />
        <div
          className="absolute top-16 -right-20 w-96 h-96 bg-blue-400/18 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '900ms' }}
        />
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 relative z-10">
        <div className="text-center mb-8 md:mb-10 lg:mb-14 animate-slide-down">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full border border-pink-200/70 bg-white/80 shadow-sm backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5 text-pink-500" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-pink-600">
              Trusted Partners
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-5">
            {title}
          </h2>
          <p className="text-[0.95rem] sm:text-base lg:text-[1.05rem] text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-10 lg:mb-14 max-w-5xl mx-auto">
          <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/80 shadow-[0_14px_40px_-22px_rgba(0,0,0,0.25)] text-center hover:shadow-[0_22px_50px_-22px_rgba(250,0,172,0.4)] transition-smooth hover:-translate-y-1">
            <ShieldCheck className="w-5 h-5 text-pink-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary mb-2">50+</div>
            <div className="text-sm text-muted-foreground font-medium">Global Brands</div>
          </div>
          <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/80 shadow-[0_14px_40px_-22px_rgba(0,0,0,0.25)] text-center hover:shadow-[0_22px_50px_-22px_rgba(250,0,172,0.4)] transition-smooth hover:-translate-y-1">
            <Globe2 className="w-5 h-5 text-blue-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary mb-2">90+</div>
            <div className="text-sm text-muted-foreground font-medium">Happy Clients</div>
          </div>
          <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/80 shadow-[0_14px_40px_-22px_rgba(0,0,0,0.25)] text-center hover:shadow-[0_22px_50px_-22px_rgba(250,0,172,0.4)] transition-smooth hover:-translate-y-1">
            <BriefcaseBusiness className="w-5 h-5 text-emerald-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary mb-2">20+</div>
            <div className="text-sm text-muted-foreground font-medium">Industries Served</div>
          </div>
          <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/80 shadow-[0_14px_40px_-22px_rgba(0,0,0,0.25)] text-center hover:shadow-[0_22px_50px_-22px_rgba(250,0,172,0.4)] transition-smooth hover:-translate-y-1">
            <Sparkles className="w-5 h-5 text-amber-500 mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary mb-2">16+</div>
            <div className="text-sm text-muted-foreground font-medium">Years Experience</div>
          </div>
        </div> */}

        <div className="rounded-[2rem] border border-white/70 bg-white/70 backdrop-blur-xl shadow-[0_24px_70px_-30px_rgba(39,78,255,0.35)] p-4 md:p-6">
          <div className="overflow-hidden relative">
            <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white/95 to-transparent z-10 hidden md:block" />
            <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white/95 to-transparent z-10 hidden md:block" />

            <div className="flex space-x-6 md:space-x-8 animate-scroll-left whitespace-nowrap py-4 md:py-5">
              {[...clients, ...clients].map((client, idx) => (
                <div
                  key={`${client.id}-${idx}`}
                  className="flex-shrink-0 flex items-center justify-center min-w-[130px] md:min-w-[190px] rounded-xl border border-white/70 bg-white/80 px-4 py-4 md:py-5 grayscale hover:grayscale-0 hover:-translate-y-1 transition-all duration-300 shadow-[0_12px_30px_-24px_rgba(0,0,0,0.35)]"
                  title={client.name}
                >
                  <AppImage
                    src={client.logo}
                    alt={client.alt}
                    width={200}
                    height={80}
                    className="h-8 md:h-12 w-auto object-contain !bg-transparent opacity-65 hover:opacity-100 transition-opacity"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <style jsx global>{`
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .animate-scroll-left {
            animation: scroll-left 60s linear infinite;
            display: flex;
            width: max-content;
          }
          .animate-scroll-left:hover {
            animation-play-state: paused;
          }
          @media (max-width: 768px) {
            .animate-scroll-left {
              animation-duration: 100s;
            }
          }
        `}</style>
      </div>
    </section>
  );
}
